import React, { useEffect, useState, useMemo } from "react";
import { Helmet } from "react-helmet-async";
import Sidenav from "../components/Sidenav";
import CreateClient from "../components/CreateClient";
import EditClient from "../components/EditClient";
import UpdateClientLogo from "../components/UpdateClientLogo"; // NEW
import DeleteClientModal from "../components/DeleteClientModal";
import "../Console.css";

/* ------------------ Inline styles for grouped card UI ------------------ */
const styles = (
  <style>{`
    .sections { display:flex; flex-direction:column; gap:18px; }

    .section {
      background:#ffffff;
      border:1px solid #e5e7eb;
      border-radius:12px;
      padding:12px 14px;
      box-shadow:0 1px 2px rgba(0,0,0,0.03), 0 8px 24px rgba(0,0,0,0.04);
    }

    .section-title {
      display:flex; align-items:center; gap:10px;
      font-size:14px; font-weight:800; color:#111827; margin:2px 0 10px 2px;
      letter-spacing:.2px;
    }
    .section-title .badge {
      display:inline-flex; align-items:center; justify-content:center;
      width:24px; height:24px; border-radius:9999px;
      background:#f3f4f6; color:#374151; font-size:12px; font-weight:800;
    }
    .section-title small {
      color:#6b7280; font-weight:700; font-size:12px;
    }

    .clients-grid { display:grid; gap:12px; grid-template-columns: repeat(3, minmax(0,1fr)); }
    @media (max-width: 1200px) { .clients-grid { grid-template-columns: repeat(2, minmax(0,1fr)); } }
    @media (max-width: 760px)  { .clients-grid { grid-template-columns: 1fr; } }

    .client-card {
      background:#fff; border:1px solid #e5e7eb; border-radius:14px;
      padding:14px; box-shadow:0 1px 2px rgba(0,0,0,0.03), 0 8px 24px rgba(0,0,0,0.04);
      color:#111827; font-size:13px; line-height:1.35;
      position:relative;
    }

    .card-head { display:flex; align-items:center; justify-content:space-between; gap:10px; margin-bottom:8px; }
    .head-left { display:flex; align-items:center; gap:10px; min-width:0; }

    .avatar {
      width:40px; height:40px; border-radius:9999px; overflow:hidden;
      display:flex; align-items:center; justify-content:center;
      font-weight:800; font-size:13px; color:#fff; text-transform:uppercase;
      flex: 0 0 40px; border:1px solid rgba(0,0,0,0.05);
      cursor: pointer; /* NEW: clickable for Update Logo */
    }
    .avatar img { width:100%; height:100%; object-fit:cover; display:block; }

    .org-name {
      font-weight:800; font-size:16px; line-height:1.15;
      white-space:nowrap; overflow:hidden; text-overflow:ellipsis;
    }

    .divider { height:1px; background:#e5e7eb; opacity:.8; margin:10px 0; }
    .meta { display:flex; align-items:center; gap:10px; flex-wrap:wrap; }

    .tag {
      display:inline-flex; align-items:center; gap:6px; padding:3px 8px;
      border-radius:9999px; background:#eef2ff; color:#1f2937; font-size:11px; font-weight:700;
    }
    .id-chip {
      display:inline-flex; align-items:center; gap:6px; padding:2px 6px;
      border-radius:8px; background:#f9fafb; color:#6b7280; font-size:11px;
    }

    /* Actions menu */
    .card-actions { position:relative; }
    .icon-ellipsis {
      display:inline-flex; align-items:center; justify-content:center;
      width:36px; height:36px; border-radius:10px;
      background:#fff; color:#111827; border:1px solid #e5e7eb; font-weight:700; font-size:14px;
    }
    .icon-ellipsis:hover { background:#f9fafb; }
    .icon-ellipsis:focus { outline:2px solid #d1d5db; }

    .menu-pop {
      position:absolute; top:38px; right:0;
      background:#fff; border:1px solid #e5e7eb; border-radius:12px;
      box-shadow:0 10px 30px rgba(0,0,0,0.08);
      min-width:180px; padding:6px; z-index:50;
    }
    .menu-item {
      width:100%; display:flex; align-items:center; gap:10px;
      border:none; background:transparent; text-align:left;
      padding:8px 10px; border-radius:8px; font-size:13px; color:#111827; font-weight:700;
    }
    .menu-item i { font-size:14px; }
    .menu-item:hover { background:#f3f4f6; }
    .menu-item.danger { color:#b91c1c; }
    .menu-item.danger:hover { background:#fff1f2; }
    .menu-item:disabled { opacity:.6; cursor:not-allowed; }

    .empty-state {
      background:#fff; border:1px dashed #e5e7eb; border-radius:12px; padding:24px;
      text-align:center; color:#6b7280; font-size:13px;
    }
  `}</style>
);

/* ------------------ Helpers ------------------ */
const PROTECTED_ORGS = new Set(["Greenbridge", "ACN"]);

function headingFor(typeKey) {
  const map = {
    brand: "Brands",
    enforcement: "Enforcement",
    judiciary: "Judiciary",
  };
  const k = (typeKey || "").toLowerCase();
  return map[k] || (k ? k.charAt(0).toUpperCase() + k.slice(1) : "Other");
}
function prettyType(t) {
  if (!t) return "—";
  const k = t.toLowerCase();
  if (k === "brand") return "Brand";
  if (k === "enforcement") return "Enforcement";
  if (k === "judiciary") return "Judiciary";
  return t.charAt(0).toUpperCase() + t.slice(1);
}
function getInitials(name) {
  if (!name) return "CL";
  const parts = name.trim().split(/\s+|[_.-]+/);
  const letters = parts
    .filter(Boolean)
    .slice(0, 2)
    .map((p) => p[0]);
  return letters.join("").toUpperCase() || "CL";
}
function colorFromString(str) {
  const colors = [
    "#4f46e5",
    "#0ea5e9",
    "#22c55e",
    "#f59e0b",
    "#ef4444",
    "#a855f7",
    "#14b8a6",
    "#f97316",
  ];
  let hash = 0;
  for (let i = 0; i < (str || "").length; i++) {
    hash = (hash << 5) - hash + str.charCodeAt(i);
    hash |= 0;
  }
  return colors[Math.abs(hash) % colors.length];
}

/* ------------------ Component ------------------ */
const Clients = ({ domain /*, userData */ }) => {
  const [organizations, setOrganizations] = useState([]);

  // menu state per card
  const [openMenuId, setOpenMenuId] = useState(null);

  // edit modal state
  const [showEditClientModal, setShowEditClientModal] = useState(false);
  const [editClientId, setEditClientId] = useState("");

  // NEW: update-logo modal state
  const [showUpdateLogoModal, setShowUpdateLogoModal] = useState(false);
  const [updateLogoClientId, setUpdateLogoClientId] = useState("");

  const [showCreateClientModal, setShowCreateClientModal] = useState(false);
  const handleCloseCreateClientModal = () => setShowCreateClientModal(false);
  const handleShowCreateClientModal = () => setShowCreateClientModal(true);

  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteOrg, setDeleteOrg] = useState(null); // { _id, organization }

  const fetchOrganizations = () => {
    fetch("/api/organizations")
      .then((response) => response.json())
      .then((data) => setOrganizations(data || []))
      .catch((error) => console.error("Error fetching organizations:", error));
  };

  useEffect(() => {
    fetchOrganizations();
  }, []);

  const handleFormSubmit = () => {
    fetchOrganizations();
  };

  const handleDeleteOrganization = (org) => {
    setDeleteOrg({ _id: org._id, organization: org.organization });
    setShowDeleteModal(true);
  };

  const handleEditOrganization = (org) => {
    setOpenMenuId(null);
    setEditClientId(org._id); // pass row id
    setShowEditClientModal(true); // open modal
  };

  // NEW: open update-logo modal
  const handleUpdateLogo = (org) => {
    setOpenMenuId(null);
    setUpdateLogoClientId(org._id);
    setShowUpdateLogoModal(true);
  };

  const handleCloseEditClientModal = () => {
    setShowEditClientModal(false);
    setEditClientId("");
  };

  // NEW: close update-logo modal
  const handleCloseUpdateLogoModal = () => {
    setShowUpdateLogoModal(false);
    setUpdateLogoClientId("");
  };

  // close menus on any outer click
  useEffect(() => {
    const onDocClick = () => setOpenMenuId(null);
    document.addEventListener("click", onDocClick);
    return () => document.removeEventListener("click", onDocClick);
  }, []);

  /* ----- Grouping & ordering ----- */
  const groups = useMemo(() => {
    const m = new Map();
    organizations.forEach((org) => {
      const t = (org.type || "other").toLowerCase();
      if (!m.has(t)) m.set(t, []);
      m.get(t).push(org);
    });
    return m;
  }, [organizations]);

  const orderedTypes = useMemo(() => {
    const preferred = ["brand", "enforcement", "judiciary"];
    const existing = new Set(Array.from(groups.keys()));
    const others = Array.from(existing)
      .filter((t) => !preferred.includes(t))
      .sort((a, b) => a.localeCompare(b));
    return preferred.filter((t) => groups.has(t)).concat(others);
  }, [groups]);

  return (
    <>
      <Helmet>
        <title>Clients | BLEEP</title>
        <meta
          name="description"
          content="Introducing BLEEP, the revolutionary crowdsourcing app designed to combat counterfeit products and services. Empowering users to report and verify the authenticity of goods, BLEEP connects you directly with brands and enforcement agents."
        />
      </Helmet>

      {styles}

      <div className="d-flex">
        <Sidenav domain={domain} />
        <div
          style={{
            flex: 1,
            position: "relative",
            height: "calc(100vh - 94px)",
            overflowY: "auto",
            overflowX: "hidden",
          }}
        >
          {/* Header Section */}
          <header
            style={{
              position: "relative",
              paddingInline: "30px",
              paddingBlock: "30px",
              color: "#fff",
              background:
                "linear-gradient(135deg, rgba(255,81,0,1) 0%, rgba(255,0,0,1) 100%)",
              overflow: "hidden",
            }}
          >
            <div
              aria-hidden
              style={{
                position: "absolute",
                inset: 0,
                backgroundImage: "url('/images/background-art.jpg')",
                backgroundRepeat: "repeat",
                backgroundSize: "400px 400px",
                backgroundPosition: "center",
                mixBlendMode: "overlay",
                opacity: 0.1,
                zIndex: 0,
                pointerEvents: "none",
              }}
            />
            <div
              className="row d-flex align-items-center"
              style={{ position: "relative", zIndex: 1 }}
            >
              <div
                className="col"
                style={{ padding: 10 }}
              >
                <div
                  className="col d-flex align-items-center"
                  style={{ color: "white" }}
                >
                  <b
                    style={{
                      fontSize: "28px",
                      textShadow: "#1c1c1c 1px 2px 0px",
                    }}
                  >
                    <i className="fas fa-handshake" /> Clients
                  </b>
                </div>
                <p
                  style={{
                    paddingTop: 8,
                    textShadow: "#1c1c1c 1px 1px 0px",
                    marginBottom: 14,
                  }}
                >
                  Manage Client profiles
                </p>
                <button
                  type="button"
                  className="btn btn-light btn-lg"
                  style={{
                    borderRadius: "30px",
                    boxShadow: "rgba(0, 0, 0, 0.25) 0px 3px 8px",
                    paddingBlock: "10px",
                    paddingInline: "30px",
                  }}
                  onClick={handleShowCreateClientModal}
                >
                  <span style={{ marginRight: 10 }}>
                    <i className="fa-solid fa-plus-circle" />
                  </span>
                  <b>Create client</b>
                </button>
              </div>
            </div>
          </header>

          {/* Grouped Cards Section */}
          <div
            className="p-4"
            style={{ backgroundColor: "#ebebeb" }}
          >
            {organizations.length === 0 ? (
              <div className="empty-state">No clients yet.</div>
            ) : (
              <div className="sections">
                {orderedTypes.map((typeKey) => {
                  const list = groups.get(typeKey) || [];
                  if (!list.length) return null;

                  return (
                    <section
                      key={typeKey}
                      className="section"
                    >
                      <h3 className="section-title">
                        <span className="badge">
                          <i className="fas fa-th-large" />
                        </span>
                        {headingFor(typeKey)}
                        <small>&nbsp;•&nbsp;{list.length}</small>
                      </h3>

                      <div className="clients-grid">
                        {list.map((org) => {
                          const isProtected = PROTECTED_ORGS.has(
                            org.organization
                          );
                          const orgName = org.organization || "—";
                          const pretty = prettyType(org.type);
                          const initials = getInitials(orgName);
                          const bg = colorFromString(orgName);

                          // If backend added logoUrl, prefer it; fall back to any legacy imageUrl
                          const logo = org.logoUrl || org.imageUrl;

                          const menuOpen = openMenuId === org._id;

                          return (
                            <article
                              key={org._id}
                              className="client-card"
                              onClick={(e) => e.stopPropagation()}
                            >
                              <div className="card-head">
                                <div
                                  className="head-left"
                                  title={orgName}
                                >
                                  <div
                                    className="avatar"
                                    style={{
                                      background: logo ? "transparent" : bg,
                                    }}
                                    aria-label={`Avatar of ${orgName}`}
                                    onClick={() => handleUpdateLogo(org)} // NEW: clicking avatar opens Update Logo
                                    role="button"
                                  >
                                    {logo ? (
                                      <img
                                        src={logo}
                                        alt={`${orgName} logo`}
                                      />
                                    ) : (
                                      <span>{initials}</span>
                                    )}
                                  </div>
                                  <div>
                                    <div className="org-name">{orgName}</div>
                                    {isProtected && (
                                      <span style={{
                                        display: "inline-flex", alignItems: "center", gap: 4,
                                        marginTop: 4, padding: "2px 7px", borderRadius: 9999,
                                        background: "#fff7f3", border: "1.5px solid rgba(255,81,0,0.3)",
                                        color: "#c13d00", fontSize: 10, fontWeight: 800,
                                        letterSpacing: ".5px", textTransform: "uppercase",
                                      }}>
                                        <i className="fas fa-lock" style={{ fontSize: 9 }} />
                                        Internal
                                      </span>
                                    )}
                                  </div>
                                </div>

                                <div className="card-actions">
                                  <button
                                    className="icon-ellipsis"
                                    aria-haspopup="menu"
                                    aria-expanded={menuOpen}
                                    title="Actions"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setOpenMenuId(menuOpen ? null : org._id);
                                    }}
                                  >
                                    <i className="fas fa-ellipsis-h" />
                                  </button>

                                  {menuOpen && (
                                    <div
                                      className="menu-pop"
                                      role="menu"
                                      onClick={(e) => e.stopPropagation()}
                                    >
                                      {/* NEW: Update logo appears above Edit */}
                                      <button
                                        className="menu-item"
                                        role="menuitem"
                                        onClick={() => handleUpdateLogo(org)}
                                        title="Update logo"
                                      >
                                        <i className="fas fa-image" />
                                        Update logo
                                      </button>

                                      <button
                                        className="menu-item"
                                        role="menuitem"
                                        onClick={() =>
                                          handleEditOrganization(org)
                                        }
                                        disabled={isProtected}
                                        title={
                                          isProtected
                                            ? "Protected"
                                            : "Edit client"
                                        }
                                      >
                                        <i className="fas fa-pen-to-square" />
                                        Edit
                                      </button>

                                      <button
                                        className="menu-item danger"
                                        role="menuitem"
                                        onClick={() => {
                                          if (isProtected) return;
                                          setOpenMenuId(null);
                                          handleDeleteOrganization(org);
                                        }}
                                        disabled={isProtected}
                                        title={
                                          isProtected
                                            ? "Protected"
                                            : "Delete client"
                                        }
                                      >
                                        <i className="fas fa-trash-alt" />
                                        Delete
                                      </button>
                                    </div>
                                  )}
                                </div>
                              </div>

                              <div className="divider" />

                              <div className="meta">
                                <span className="tag">
                                  <i className="fas fa-id-badge" />
                                  {pretty}
                                </span>
                                {org._id && (
                                  <span
                                    className="id-chip"
                                    title={org._id}
                                  >
                                    ID: {String(org._id).slice(-6)}
                                  </span>
                                )}
                              </div>

                              {(org.address || org.contactEmail || org.primaryPhone || org.secondaryPhone) && (
                                <>
                                  <div className="divider" />
                                  <div style={{ display: "flex", flexDirection: "column", gap: 4, fontSize: 12, color: "#374151" }}>
                                    {org.address && (
                                      <span title="Address">
                                        <i className="fas fa-map-marker-alt" style={{ width: 14, color: "#9ca3af" }} />
                                        {" "}{org.address}
                                      </span>
                                    )}
                                    {org.contactEmail && (
                                      <span title="Contact email">
                                        <i className="fas fa-envelope" style={{ width: 14, color: "#9ca3af" }} />
                                        {" "}
                                        <a href={`mailto:${org.contactEmail}`} style={{ color: "#2563eb", textDecoration: "none" }}>
                                          {org.contactEmail}
                                        </a>
                                      </span>
                                    )}
                                    {org.primaryPhone && (
                                      <span title="Primary phone">
                                        <i className="fas fa-phone" style={{ width: 14, color: "#9ca3af" }} />
                                        {" "}{org.primaryPhone}
                                      </span>
                                    )}
                                    {org.secondaryPhone && (
                                      <span title="Secondary phone">
                                        <i className="fas fa-phone-alt" style={{ width: 14, color: "#9ca3af" }} />
                                        {" "}{org.secondaryPhone}
                                      </span>
                                    )}
                                  </div>
                                </>
                              )}
                            </article>
                          );
                        })}
                      </div>
                    </section>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Modals */}
      <CreateClient
        domain={domain}
        show={showCreateClientModal}
        handleClose={handleCloseCreateClientModal}
        handleFormSubmit={handleFormSubmit}
      />

      <EditClient
        domain={domain}
        show={showEditClientModal}
        clientId={editClientId}
        organizationId={editClientId}
        handleClose={handleCloseEditClientModal}
        handleFormSubmit={() => {
          fetchOrganizations();
          handleCloseEditClientModal();
        }}
      />

      {/* NEW: Update logo modal */}
      <UpdateClientLogo
        domain={domain}
        show={showUpdateLogoModal}
        clientId={updateLogoClientId}
        organizationId={updateLogoClientId}
        handleClose={() => {
          handleCloseUpdateLogoModal();
        }}
        handleFormSubmit={() => {
          // refresh list to show new logo immediately
          fetchOrganizations();
          handleCloseUpdateLogoModal();
        }}
      />

      <DeleteClientModal
        show={showDeleteModal}
        handleClose={() => { setShowDeleteModal(false); setDeleteOrg(null); }}
        clientId={deleteOrg?._id}
        clientName={deleteOrg?.organization}
        onDeleted={fetchOrganizations}
      />
    </>
  );
};

export default Clients;
